import React, { forwardRef, memo, useState } from "react";
import cx from "classnames";
import { Button, Heading, ThemeStore, ThemeMode } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import Joyride, {
  ACTIONS,
  BeaconRenderProps,
  CallBackProps,
  EVENTS,
  Step,
  TooltipRenderProps,
} from "react-joyride";
import TourTooltipBeacon from "../tour-tooltip-beacon";
import styles from "./tourTooltip.module.scss";

interface ITourTooltip {
  onClose: () => void;
  onToolipOpen?: () => void;
  startTour: boolean;
  steps: Step[];
  spotlightPadding?: number;
  tooltipClassName?: string;
  beaconClassName?: string;
  theme?: ThemeMode;
}

const TourTooltip: React.FC<ITourTooltip> = (props: ITourTooltip) => {
  const {
    onClose,
    onToolipOpen,
    startTour,
    steps,
    spotlightPadding = 8,
    tooltipClassName,
    beaconClassName,
    theme,
  } = props;
  const [indexTour, setIndexTour] = useState<number>(0);

  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);

  const handleClose = () => {
    setIndexTour(0);
    window.scrollTo(0, 0);
    onClose();
  };

  const BeaconComponent = memo(
    forwardRef<HTMLButtonElement, BeaconRenderProps>((props, ref) => {
      const beaconTheme = theme || themeMode;

      return (
        <button ref={ref} {...props} className={cx(styles.tourTooltip_beacon, beaconClassName)}>
          <TourTooltipBeacon mode={beaconTheme} />
        </button>
      );
    }),
  );

  const handleCallback = (data: CallBackProps) => {
    const { action, index, lifecycle, type } = data;
    if (([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND] as string[]).includes(type)) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setIndexTour(nextIndex);
    }

    if (lifecycle === "init" && action !== "start") {
      handleClose();
    }

    if (lifecycle === "tooltip") {
      onToolipOpen?.();
    }
  };

  const TourTooltipComponent = (props: TooltipRenderProps) => {
    const { backProps, closeProps, continuous, index, primaryProps, step, tooltipProps } = props;

    return (
      <section className={cx(styles.tourTooltip, tooltipClassName)} {...tooltipProps}>
        <header className={styles.tourTooltip_header}>
          <div className={styles.tourTooltip_headerClose}>
            <button className={styles.tourTooltip_headerCloseButton} onClick={onClose}>
              <Icon name="close" size="md" color="neutral-70"></Icon>
            </button>
          </div>
          {step.title && (
            <Heading type="h2" className={styles.tourTooltip_headerTitle}>
              {step.title}
            </Heading>
          )}
        </header>
        <section className={styles.tourTooltip_content}>{step.content}</section>
        <footer className={styles.tourTooltip_buttons}>
          {index > 0 && (
            <Button variant="secondary" leftIcon="chevrons-left" {...backProps}>
              {backProps?.title}
            </Button>
          )}
          {continuous && (
            <Button variant="primary" {...primaryProps}>
              {primaryProps?.title}
            </Button>
          )}
          {!continuous && (
            <Button variant="primary" {...closeProps}>
              {closeProps?.title}
            </Button>
          )}
        </footer>
      </section>
    );
  };

  return (
    <Joyride
      callback={handleCallback}
      continuous={true}
      disableOverlayClose={true}
      disableScrolling={true}
      hideCloseButton
      run={startTour}
      scrollOffset={100}
      scrollToFirstStep
      spotlightPadding={spotlightPadding}
      stepIndex={indexTour}
      steps={steps}
      beaconComponent={BeaconComponent}
      styles={{
        options: {
          zIndex: 1000,
          arrowColor: "var(--color-neutral-00)",
          overlayColor: "rgba(49, 61, 73, 0.7)",
        },
        beacon: {
          zIndex: 999,
        },
      }}
      tooltipComponent={TourTooltipComponent}
    />
  );
};

export default TourTooltip;
